import { BaseViewStore } from 'core/stores';
import { OnHoldForm } from 'modules/order/forms';
import { action, makeObservable } from 'mobx';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { isSome, notificationError, translate } from 'core/utils';
import { fulfillmentOrderStatus, onHoldReason, onHoldStatuses } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';

class OnHoldViewStore extends BaseViewStore {
	selectedItem = null;
	selectedItems = null;
	debouncedOnHoldSearch = null;

	form = new OnHoldForm({
		onSuccess: async () => {
			if (this.selectedItems == null) {
				await this.onHold();
			} else {
				await this.onHoldBulk();
			}
			this.modalParams.success();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.initialize(_.get(this.modalParams, 'data.selectedItemId'), _.get(this.modalParams, 'data.selectedItemIds'));
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		if (selectedItemId != null) {
			await this.getFulfillmentOrder(selectedItemId);
		} else {
			await this.findFulfillmentOrders(selectedItemIds);
		}
		this.onHoldReasonDropdownStore = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			value: this.setInitialOnHoldReason(),
			field: this.form.$('onHoldReasonId'),
			lookupRoute: lookupRouteName.onHoldReason,
			filterCallback: this.filterOnHoldReason,
		});
		this.resume();
	};

	@action.bound
	setInitialOnHoldReason = () => {
		if (this.selectedItem && this.selectedItem.onHoldReason != null) {
			return {
				value: this.selectedItem.onHoldReasonId,
				label: this.selectedItem.onHoldReason.name,
			};
		}

		if (this.selectedItems) {
			let selectedItemsWithOnHoldReason = this.selectedItems.filter(item => isSome(item.onHoldReason));

			if (selectedItemsWithOnHoldReason != null && selectedItemsWithOnHoldReason.length > 0) {
				let onHoldReason = selectedItemsWithOnHoldReason[0].onHoldReason;
				let selectedItemsWithSameOnHoldReason = selectedItemsWithOnHoldReason.filter(
					item => item.onHoldReasonId == onHoldReason.id
				);

				if (selectedItemsWithSameOnHoldReason.length == selectedItemsWithOnHoldReason.length) {
					return onHoldReason;
				}
			}
		}
	};

	@action.bound
	findFulfillmentOrders = async selectedItemIds => {
		this.selectedItems = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: ['fulfillmentOrderStatus', 'onHoldReason'],
		})).item;
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: ['fulfillmentOrderStatus', 'onHoldReason'],
		});
	};

	@action.bound
	filterOnHoldReason(options = []) {
		if (this.selectedItem && this.selectedItem.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.entered) {
			const optionIdx = _.findIndex(options, item => {
				return item.abrv == onHoldReason.expensiveService;
			});
			if (optionIdx >= 0) {
				options.splice(optionIdx, 1);
			}
		}

		return options.map(item => {
			return {
				value: item.id,
				label: item.name,
				extra: item,
			};
		});
	}

	@action.bound
	onHold = async () => {
		try {
			const { onHoldReasonId } = this.form.values();
			const onHoldParams = { fulfillmentOrderId: this.selectedItem.id, onHoldReasonId: onHoldReasonId };
			await this.fulfillmentOrderStore.onHold(onHoldParams, sourceOfChange.onHoldAction);
			if (this.onHoldReasonDropdownStore.value != null) {
				this.rootStore.notificationStore.success(['ONHOLDVIEW.SUCCESS', { id: this.selectedItem.fulfillmentOrderId }]);
			} else {
				this.rootStore.notificationStore.success([
					'ONHOLDVIEW.CLEAREDONHOLD',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
		} catch (err) {
			if (err.statusCode == 400 && _.get(err, 'data.message') == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	@action.bound
	onHoldBulk = async () => {
		try {
			const { onHoldReasonId } = this.form.values();
			const selectedOrderIds = [];
			const notProcessableOrders = [];
			this.selectedItems.map(selectedItem => {
				let index = _.findIndex(onHoldStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					selectedOrderIds.push(selectedItem.id);
				} else {
					notProcessableOrders.push(selectedItem);
				}
			});
			const onHoldBulkParams = { fulfillmentOrderIds: selectedOrderIds, onHoldReasonId: onHoldReasonId };
			this.modalParams.close();
			const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.ONHOLD'));
			const count = await this.fulfillmentOrderStore.onHoldBulk(onHoldBulkParams, sourceOfChange.onHoldBulkAction);
			this.rootStore.notificationStore.dismiss(notificationId);
			if (count > 0) {
				if (count == selectedOrderIds.length) {
					if (this.onHoldReasonDropdownStore.value != null) {
						this.rootStore.notificationStore.success(['ONHOLDVIEW.SUCCESSBULK', { count: count }]);
					} else {
						this.rootStore.notificationStore.success(['ONHOLDVIEW.CLEAREDONHOLDBULK', { count: count }]);
					}
				} else {
					const totalOrdersNotUpdated = selectedOrderIds.length - count + notProcessableOrders.length;
					this.rootStore.notificationStore.success(['ONHOLDVIEW.PARTIALSUCCESSBULK', { count: totalOrdersNotUpdated }]);
				}
			} else {
				this.rootStore.notificationStore.error(['ONHOLDVIEW.NOVALIDORDERSELECTED', { count: count }]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};
}

export default OnHoldViewStore;
